import React, { useState, useEffect } from 'react';
import { 
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Box,
  IconButton,
  useTheme,
  TextField,
  InputAdornment,
  Paper
} from '@mui/material';
import { 
  ElectricMeter as MeterIcon,
  Share as ShareIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
const ApiUrl = process.env.REACT_APP_API_URL;

const Entities = () => {
  const [Entities, setEntities] = useState([]);
  const [filteredEntities, setFilteredEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        const response = await fetch(`${ApiUrl}/entity-history/history`);
        const data = await response.json();
        setEntities(data);
        setFilteredEntities(data);
        setLoading(false);
      } catch (error) {
        setError("Failed to fetch energy meters.");
        setLoading(false);
      }
    };

    fetchEntities();
  }, []);

  // Filter entities based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredEntities(Entities);
    } else {
      const filtered = Entities.filter(entity => 
        entity.deviceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entity.entityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entity.state.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEntities(filtered);
    }
  }, [searchTerm, Entities]);


  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const getStateColor = (state) => {
    if (state === "ON") return theme.palette.success.main;
    if (state === "OFF") return theme.palette.error.main;
    return '#482880';
  };
  
  // Function to determine the progress value based on energy state
  const getProgressValue = (state) => {
    if (state === "ON" || state === "OFF") return 100;
    const value = parseFloat(state);
    if (!isNaN(value)) {
      return 100;
    }
    return 0;
  };
  if (loading) {
    return (
     
      <Box display="flex" flexDirection="column" alignItems="center" p={4}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading Entities data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <>
   
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Energy Meters Dashboard
      </Typography>
      
      {/* Search Filter */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField
          fullWidth
          placeholder="Search entities by device name, entity name, or state..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
        />
        {searchTerm && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Showing {filteredEntities.length} of {Entities.length} entities
          </Typography>
        )}
      </Paper>
      
      <Card sx={{ mb: 4, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
        <CardContent>
          <Box display="flex" alignItems="center">
            <MeterIcon sx={{ fontSize: 40, mr: 2 }} />
            <Typography variant="h5">
             All Entities With Devices
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {filteredEntities.length === 0 ? (
          <Grid item xs={12}>
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No entities found matching your search criteria
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting your search terms
              </Typography>
            </Card>
          </Grid>
        ) : (
          filteredEntities.map((entity) => (
          <Grid item xs={12} sm={5} md={3} key={entity._id}>
            <Card 
              elevation={3}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)'
                }
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Box position="relative" display="inline-flex" mr={2}>
                  <CircularProgress
                variant="determinate"
                value={getProgressValue(entity.state)}
                size={60}
                sx={{ color: getStateColor(entity.state) }}
              />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="caption" component="div" color="text.secondary">
                        {entity.state}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {entity.deviceName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {entity.entityName}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
              <CardActions sx={{ mt: 'auto', justifyContent: 'space-between' }}>
                <Button
                  variant="contained"
                  size="small"
                  endIcon={<InfoIcon />}
                  onClick={() => navigate(`/entities/history/detail/${entity._id}`, {
                    state: {
                      deviceName: entity.deviceName,
                      entityName: entity.entityName,
                      state: entity.state,
                      entityId: entity._id,
                    },
                  })}
                >
                  More Details
                </Button>
              </CardActions>
            </Card>
          </Grid>
          ))
        )}
      </Grid>
    </Box>
    </>
  );
};

export default Entities;